/**
 * Report payload construction for submission
 *
 * Extracted from useReportSubmission.js. Pure functions that resolve tag-based
 * routing and assemble the report body (including the HTML email details block)
 * posted to /items.
 */
import { v4 as uuidv4 } from 'uuid';

import Util from '../util';

import { ANONYMOUS_REPORTER } from './constants';

/**
 * Resolve the organization a report routes to based on its tags.
 *
 * @param {Object|Array} routing - Tag → organization routing map from org config
 * @param {string[]} allTags - Combined KPI + location tags on the report
 * @returns {string|null} The routed organization, or null if no tag matches
 */
export function resolveRoutedOrg(routing, allTags) {
  const routeKey = routing ? Object.keys(routing).find((key) => allTags.includes(key)) : null;
  return routeKey ? routing[routeKey] : null;
}

/**
 * Build the report payload posted to /items.
 *
 * @param {Object} params
 * @param {string} params.description - Report description
 * @param {string} params.email - Contact email entered on the form
 * @param {boolean} params.anonymous - Whether the reporter stays anonymous
 * @param {boolean} params.isUrgent - Urgent flag
 * @param {boolean} params.isTest - Test-report flag
 * @param {string[]} params.KPIS - Selected hazard classification tags
 * @param {string[]} params.Locations - Selected location tags
 * @param {Object|Array} params.routing - Tag → organization routing map
 * @param {string} params.OrgFilterValue - Organization selected in the form dropdown
 * @param {string} params.organization - Default organization
 * @param {string} params.userEmail - Authenticated user's email
 * @param {string[]} params.fullImageURLs - Full-size uploaded image URLs (for email embeds)
 * @param {string[]} params.fulfilledImageURLs - Uploaded image URLs stored on the report
 * @returns {{ reportData: Object, routedOrg: string|null }}
 */
export function buildReportPayload({
  description,
  email,
  anonymous,
  isUrgent,
  isTest,
  KPIS,
  Locations,
  routing,
  OrgFilterValue,
  organization,
  userEmail,
  fullImageURLs,
  fulfilledImageURLs,
}) {
  const reportId = uuidv4();
  const allTags = [...KPIS, ...Locations];
  const routedOrg = resolveRoutedOrg(routing, allTags);
  const shortReportId = Util.formatUuid(reportId);

  const reportOrg = routedOrg || OrgFilterValue || organization;
  const portalLink = `https://monetareport.net/admin-report-details?uuid=${reportId}&defaultOrganization=${reportOrg}&noApp=true`;

  let details =
    // Pinned to English: this is an email body whose surrounding labels are
    // English, and the recipient is not the submitter. Per-recipient
    // localization is a backend concern.
    '<b>Sent:</b> ' + Util.timeConverter(Date.now(), { locale: 'en' }) + '<br/>' +
    '<b>Report ID:</b> ' + shortReportId + '<br/>' +
    '<b>Description</b>: ' + description + '<br />' +
    '<b>Hazard Classifications: </b>' + KPIS.join(', ') + '<br />' +
    '<b>Locations: </b>' + Locations.join(', ') + '<br />' +
    '<b>View in portal:</b> <a href="' + portalLink + '">' + portalLink + '</a>';

  if (!anonymous) {
    details += '<br/> <b>Reporter:</b> ' + userEmail;
  }

  if (fullImageURLs.length > 0) {
    details += '<br/><br/><b>Images:</b><br/>';
    for (const img in fullImageURLs) {
      details += `<img src="cid:unique@screenshot-${img}" style="width: 100px"/><br/>`;
    }
  }

  const reportData = {
    user: userEmail,
    timestamp: Date.now(),
    comments: [],
    firstCommentDate: null,
    commentWaitTime: null,
    content: description,
    email,
    organization: reportOrg,
    uuid: reportId,
    reporter: anonymous ? ANONYMOUS_REPORTER : userEmail,
    isTest,
    isUrgent,
    KPIs: KPIS,
    HROs: [],
    locations: Locations,
    images: fulfilledImageURLs,
    shortReportId,
    message: 'A new near-miss report was submitted.<br/><br/>',
    details,
    fullImageURLs,
  };

  return { reportData, routedOrg };
}
